import { useState } from "react";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, TrendingUp, AlertCircle, CheckCircle, FileText } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CaseAnalyzer = ({ user, onLogout }) => {
  const [formData, setFormData] = useState({
    case_type: "civil",
    case_details: ""
  });
  const [analysis, setAnalysis] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/ai/analyze-case`, formData);
      setAnalysis(response.data.analysis);
      toast.success("تم تحليل القضية بنجاح");
    } catch (error) {
      toast.error("خطأ في تحليل القضية: " + (error.response?.data?.detail || "حاول مرة أخرى"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-8" data-testid="case-analyzer-page">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">محلل القضايا الذكي</h1>
          <p className="text-lg text-slate-600">تحليل شامل للقضايا مع توصيات استراتيجية باستخدام الذكاء الاصطناعي</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Analysis Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-indigo-600" />
                تفاصيل القضية
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="case_type">نوع القضية *</Label>
                  <Select
                    value={formData.case_type}
                    onValueChange={(value) => setFormData({ ...formData, case_type: value })}
                  >
                    <SelectTrigger id="case_type" data-testid="analyzer-case-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="civil">مدني</SelectItem>
                      <SelectItem value="criminal">جنائي</SelectItem>
                      <SelectItem value="commercial">تجاري</SelectItem>
                      <SelectItem value="labor">عمل</SelectItem>
                      <SelectItem value="personal_status">أحوال شخصية</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="case_details">وصف القضية التفصيلي *</Label>
                  <Textarea
                    id="case_details"
                    placeholder="قدم وصفاً شاملاً للقضية يتضمن: الأطراف، الوقائع، المستندات، الخلاف، والظروف المحيطة..."
                    rows={12}
                    value={formData.case_details}
                    onChange={(e) => setFormData({ ...formData, case_details: e.target.value })}
                    required
                    data-testid="case-details-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full gap-2"
                  disabled={loading || !formData.case_details.trim()}
                  data-testid="analyze-button"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      جاري التحليل...
                    </>
                  ) : (
                    <>
                      <TrendingUp className="w-4 h-4" />
                      تحليل القضية
                    </>
                  )}
                </Button>
              </form>

              {/* Sample Cases */}
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-2">💡 نصيحة:</h3>
                <p className="text-sm text-blue-800">
                  لنتائج أفضل، قدم تفاصيل دقيقة تشمل: أسماء الأطراف، تواريخ الأحداث، المستندات المتوفرة، 
                  الشهود، المبالغ المالية (إن وجدت)، والظروف الخاصة بالقضية.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Analysis Results */}
          <Card className="lg:sticky lg:top-24 lg:max-h-[calc(100vh-8rem)] overflow-hidden flex flex-col">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-6 h-6 text-indigo-600" />
                نتائج التحليل
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto">
              {analysis ? (
                <div className="space-y-4" data-testid="analysis-results">
                  <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle className="w-5 h-5 text-indigo-600" />
                      <h3 className="font-bold text-indigo-900">التحليل الكامل:</h3>
                    </div>
                    <div className="text-slate-800 whitespace-pre-wrap leading-relaxed">
                      {analysis}
                    </div>
                  </div>

                  {/* Key Points */}
                  <div className="grid grid-cols-1 gap-3">
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="font-semibold text-green-900">نقاط القوة</span>
                      </div>
                      <p className="text-sm text-green-700">تحليل نقاط القوة موجود في النص أعلاه</p>
                    </div>

                    <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-center gap-2 mb-1">
                        <AlertCircle className="w-4 h-4 text-amber-600" />
                        <span className="font-semibold text-amber-900">نقاط الضعف</span>
                      </div>
                      <p className="text-sm text-amber-700">تحليل نقاط الضعف موجود في النص أعلاه</p>
                    </div>

                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center gap-2 mb-1">
                        <TrendingUp className="w-4 h-4 text-blue-600" />
                        <span className="font-semibold text-blue-900">التوصيات</span>
                      </div>
                      <p className="text-sm text-blue-700">التوصيات الاستراتيجية موجودة في النص أعلاه</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-400">
                  <div className="text-center">
                    <TrendingUp className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>لم يتم تحليل أي قضية بعد</p>
                    <p className="text-sm">املأ النموذج واضغط "تحليل القضية"</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CaseAnalyzer;
